import { useRef, useState, useMemo } from "react";
import {
  Box,
  Text,
  Flex,
  IconButton,
  Button,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Progress,
  Divider,
} from "@chakra-ui/react";
import { IoMdClose } from "react-icons/io";
import { TiTick } from "react-icons/ti";
import { ComponentLoading } from "../loading/ComponentLoading";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

export const KeywordDialogBox = ({
  keywords,
  openDialog,
  setOpenDialog,
  loading,
}: {
  keywords: string[];
  openDialog: boolean;
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  loading?: boolean;
}) => {
  const onClose = () => setOpenDialog(false);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const AlertDialogContentAny = AlertDialogContent as any;
  const AlertDialogHeaderAny = AlertDialogHeader as any;
  const AlertDialogBodyAny = AlertDialogBody as any;
  const AlertDialogFooterAny = AlertDialogFooter as any;
  const DividerAny = Divider as any;
  const IconButtonDialogAny = IconButton as any;
  const TextDialogAny = Text as any;

  const PAGE_SIZE = 300;
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(keywords.length / PAGE_SIZE);
  const paginatedKeywords = useMemo(() => {
    const start = page * PAGE_SIZE;
    return keywords.slice(start, start + PAGE_SIZE);
  }, [keywords, page]);

  const handlePrev = () => setPage((p) => Math.max(0, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));

  return (
    <AlertDialog
      isOpen={openDialog}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContentAny width="100%" position="relative">
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              maxHeight="400px"
              backgroundColor="white"
              opacity={1}
            >
              <ComponentLoading text="Loading Keywords" />
            </Box>
          ) : (
            <>
              <IconButtonDialogAny
                aria-label="Close dialog"
                icon={<IoMdClose />}
                onClick={onClose}
                position="absolute"
                top={2}
                right={2}
                size="sm"
                variant="ghost"
                color="primary.dark"
                zIndex={1}
                ref={cancelRef}
              />
              <AlertDialogHeaderAny
                fontSize="md"
                width="100%"
                textAlign="left"
                pl={2}
                pr={8}
                pt={8}
                pb={2}
                ml={5}
              >
                Keywords in file:
              </AlertDialogHeaderAny>
              <DividerAny />
              <AlertDialogBodyAny maxHeight="400px" overflowY="auto">
                <Box overflow="scroll">
                  {paginatedKeywords.map((keywordText, index) => (
                    <Box
                      key={page * PAGE_SIZE + index}
                      p={3}
                      borderBottomWidth="1px"
                      borderColor="gray.200"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Text fontSize={14} mr={4} flex={1}>
                        {keywordText}
                      </Text>
                      <Box
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        width={7}
                        height={7}
                        borderRadius="full"
                        backgroundColor="#D1FAE5"
                      >
                        <TiTick color="#22543D" size={18} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </AlertDialogBodyAny>
              <DividerAny />
              <AlertDialogFooterAny>
                <Flex
                  align="center"
                  justifyContent="space-between"
                  width="100%"
                  direction={"column"}
                >
                  <Flex
                    width="100%"
                    align="center"
                    justifyContent="space-between"
                  >
                    <Button variant="blueButton" onClick={onClose}>
                      Confirm
                    </Button>
                    <Flex align="center" width="220px" direction="column">
                      <Flex justifyContent="space-between" width="220px" mb={1}>
                        <TextDialogAny
                          fontSize={12}
                          color="formLabelText"
                          mb={1}
                        >
                          {`${keywords.length}/ 20,000 `}
                        </TextDialogAny>
                        <TextDialogAny
                          fontSize={12}
                          color="formLabelText"
                          mb={1}
                        >
                          Keyword limit
                        </TextDialogAny>
                      </Flex>
                      <Box width="220px">
                        <Progress
                          size="xs"
                          value={keywords.length}
                          max={20000}
                        />
                      </Box>
                    </Flex>
                  </Flex>
                  <DividerAny mt={5} width="100%" />
                  {totalPages > 1 && (
                    <Flex
                      mt={3}
                      width="100%"
                      justifyContent="center"
                      alignItems="center"
                      gap={2}
                    >
                      <IconButtonDialogAny
                        icon={<FaChevronLeft />}
                        onClick={handlePrev}
                        disabled={page === 0}
                        variant="ghost"
                      />
                      <Text fontSize={12} mx={2}>
                        Page {page + 1} of {totalPages}
                      </Text>
                      <IconButtonDialogAny
                        icon={<FaChevronRight />}
                        onClick={handleNext}
                        disabled={page === totalPages - 1}
                        variant="ghost"
                      />
                    </Flex>
                  )}
                </Flex>
              </AlertDialogFooterAny>
            </>
          )}
        </AlertDialogContentAny>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
